import { MouseEvent, useMemo, useRef } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useQueryClient } from 'react-query';

import {
  Box,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Skeleton,
  Stack,
  Tag,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Difficulty1Icon,
  Difficulty2Icon,
  Difficulty3Icon,
  MoreHorizontalIcon,
  TimeIcon,
} from '@udacity/chakra-uds-icons';
import { BreakpointValue, focusStyles } from '@udacity/chakra-uds-theme';

import { ButtonLink } from '~/components/button-link';
import { PlayButton } from '~/components/play-button';
import { PlayProgressBar } from '~/components/play-progress-bar';
import { Rating } from '~/components/rating';
import { ShinyHoverEffect } from '~/components/shiny-hover-effect';
import { trackEvent } from '~/features/analytics/track-event';
import { SubscriptionPausedModal } from '~/features/dashboard/components/subscription-paused-modal';
import { publicEnv } from '~/features/environment/public';
import { FavoriteButton } from '~/features/favorites/components/favorite-button';
import { FavoriteMenuItem } from '~/features/favorites/components/favorite-menu-item';
import { i18n } from '~/features/internationalization/internationalization';
import { Subscription } from '~/features/payment/models/subscription';
import { REVIEW_MINIMUM_THRESHOLD } from '~/features/program/constants';
import {
  DELETE_ENROLLMENT_POLICY_ERROR,
  deleteEnrollment,
  DeleteEnrollmentError,
} from '~/features/program/controllers/delete-enrollment';
import { useCourseLink } from '~/features/program/hooks/use-course-link';
import { useEnrollments } from '~/features/program/hooks/use-enrollments';
import { useEnrollmentsProgress } from '~/features/program/hooks/use-enrollments-progress';
import { EnrollmentsProgress } from '~/features/program/models/enrollments-progress';
import { Enrollment } from '~/features/program/models/program-enrollment';
import { getDifficultyLevel } from '~/features/program/utils/get-difficulty-data';
import { getRawDuration } from '~/features/program/utils/get-raw-duration';
import { useIsConnectLearner } from '~/features/sessions/hooks/use-is-connect-learner';
import { useUser } from '~/features/user/hooks/use-user';
import { useI18n } from '~/hooks/use-i18n';

import { useLocaleCatalogTitleSummary } from '../hooks/use-locale-catalog-title-summary';
import { CatalogCardItem } from '../models/catalog-card-item';
import { CatalogItem } from '../models/catalog-item';
import { CatalogSearchItem, CatalogSearchResults } from '../models/catalog-search-result';

export type CatalogCardWithCatalogItem = {
  catalogItem: CatalogItem | CatalogCardItem | CatalogSearchItem;
  basicItem?: never;
  width?: string | BreakpointValue<string>;
  minWidth?: string | BreakpointValue<string>;
  maxWidth?: string | BreakpointValue<string>;
  grow?: boolean;
  priority?: boolean;
  imageQuality?: number;
  subscription?: Subscription | null;
  enrollment?: Enrollment;
  enrollmentProgress?: EnrollmentsProgress['programs'][number];
  onClick?: (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
};

export type CatalogCardWithBasicItem = {
  catalogItem?: never;
  basicItem: {
    title: string;
    key: string;
  };
  width?: string | BreakpointValue<string>;
  minWidth?: string | BreakpointValue<string>;
  maxWidth?: string | BreakpointValue<string>;
  priority?: boolean;
  grow?: boolean;
  subscription?: never;
  enrollment?: Enrollment;
  enrollmentProgress?: EnrollmentsProgress['programs'][number];
  onClick?: (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
};

export function ProgramCatalogCard(props: CatalogCardWithCatalogItem | CatalogCardWithBasicItem) {
  const { t } = useI18n();
  const hasCatalogItem = isCatalogCardWithCatalogItem(props);
  const { catalogTitle } = useLocaleCatalogTitleSummary({
    catalogItem: props.catalogItem,
    basicItem: props.basicItem,
  });
  const { userId, userCatalog, userType } = useUser();
  const { isConnectLearner } = useIsConnectLearner();
  const courseSlug = props.catalogItem?.slug ? props.catalogItem.slug : `--${props.catalogItem?.key}`;
  const courseLink = useCourseLink({ courseSlug });

  const { enrollment: serverSideEnrollment, enrollmentProgress: serverSideEnrollmentProgress } = props;
  const { enrollmentsProgress: fetchedEnrollmentsProgress } = useEnrollmentsProgress();
  const { enrollment: fetchedEnrollment, enrollmentsIsLoading } = useEnrollments(
    hasCatalogItem ? props.catalogItem.key : props.basicItem.key
  );
  const enrollment = serverSideEnrollment || fetchedEnrollment;
  const programProgress =
    serverSideEnrollmentProgress ||
    fetchedEnrollmentsProgress?.programs.find(
      (program) => program.key === (hasCatalogItem ? props.catalogItem.key : props.basicItem.key)
    );

  const localeRawDuration = props?.catalogItem?.rawDurationDisplay
    ? getRawDuration(props.catalogItem.rawDurationDisplay, t)
    : null;
  const localeDifficultyLevel = hasCatalogItem ? getDifficultyLevel(props.catalogItem.difficultyLevel, t) : null;

  const hasReviews = hasCatalogItem
    ? props.catalogItem.reviewSummary.numberOfReviews >= REVIEW_MINIMUM_THRESHOLD
    : false;

  const { subscription } = props;
  const isConsumerSubscriptionPaused = subscription?.status === 'paused' && enrollment?.state === 'PAUSED';
  const formattedResumeDate = subscription?.resumeAt
    ? new Date(subscription?.resumeAt).toLocaleDateString(i18n.language)
    : '';
  const { isOpen: isPausedModalOpen, onOpen: openPausedModal, onClose: closePausedModal } = useDisclosure();

  const fallbackImage = '/images/program-fallbacks/8.png';
  const enterpriseData = hasCatalogItem && 'enterprise' in props.catalogItem && props.catalogItem.enterprise;

  const notStarted = !programProgress || programProgress.completion === 0;
  const isInProgress = !!programProgress && programProgress.completion > 0 && programProgress.completion < 100;

  const isReadyForGraduation =
    enrollment &&
    enrollment.isReadyForGraduation &&
    !enrollment.isGraduated &&
    'isGraduationEnabled' in enrollment &&
    enrollment.isGraduationEnabled;

  const isGraduated =
    !!enrollment && 'isGraduationEnabled' in enrollment && enrollment.isGraduationEnabled && enrollment.isGraduated;

  const Tags = useMemo(() => {
    const baseStyles = {
      borderRadius: '4px',
      padding: '4px 8px',
      variant: 'no-stroke',
      boxShadow: 'none',
    };

    if (enrollment) {
      return (
        <Tag bgColor='gray.300' {...baseStyles}>
          {t('program.enrolled')}
        </Tag>
      );
    }
    if (
      !enrollment &&
      enterpriseData &&
      enterpriseData.applicationIds.length > 0 &&
      (enterpriseData.enrollable || !enterpriseData.enrollable)
    ) {
      return (
        <Tag bgColor='gray.300' {...baseStyles}>
          {t('program.applyForAccess')}
        </Tag>
      );
    }
    if (!enrollment && enterpriseData && enterpriseData.enrollable) {
      return (
        <Tag bgColor='white' {...baseStyles}>
          {t('program.enrollNow')}
        </Tag>
      );
    }
    if (!enrollment && enterpriseData && !enterpriseData.enrollable) {
      return (
        <Tag bgColor='gray.300' {...baseStyles}>
          {t('program.requestAccess')}
        </Tag>
      );
    }
    return null;
  }, [enrollment, enterpriseData, t]);

  const url = useMemo(() => {
    if (enrollmentsIsLoading) return undefined;
    if (enrollment) return `/enrollment/${enrollment.key}`;

    if (hasCatalogItem) {
      return courseLink;
    }
    return undefined;
  }, [courseLink, enrollment, enrollmentsIsLoading, hasCatalogItem]);

  const shouldSeeUnenrollOption = useMemo(() => {
    return (
      enrollment && !isConnectLearner && (userType !== 'enterprise' || userCatalog?.catalogUrl?.includes('costco'))
    );
  }, [enrollment, isConnectLearner, userCatalog?.catalogUrl, userType]);

  return (
    <Box
      as='article'
      bg='white'
      border='1px solid'
      borderColor='gray.200'
      borderRadius='6px'
      flex={props.grow ? { base: 'unset', xl: 1 } : 'unset'}
      flexShrink={0}
      height='292px'
      maxWidth={props.maxWidth}
      minWidth={props.minWidth}
      p={1}
      position='relative'
      role='group'
      transition='all 0.3s ease-in-out'
      width={props.width}
      _hover={{
        boxShadow: 'lg',
      }}
      onClick={props.onClick}
    >
      <Flex flexDir='column' minHeight='100%'>
        <Flex
          borderTopRadius='base'
          padding='8px'
          {...focusStyles('blue.500')}
          flex='0 0 100px'
          height='100px'
          overflow='hidden'
        >
          <ShinyHoverEffect />
          {hasCatalogItem && props.catalogItem.image && typeof props.catalogItem.image === 'string' && (
            <Image
              alt=''
              priority={Boolean(props.priority)}
              quality={75}
              sizes='360px'
              src={props.catalogItem.image}
              style={{ objectFit: 'cover', borderRadius: '4px', pointerEvents: 'none' }}
              fill
            />
          )}
          {!props.catalogItem?.image && (
            <Image
              alt=''
              priority={Boolean(props.priority)}
              quality={75}
              sizes='360px'
              src={fallbackImage}
              style={{ objectFit: 'cover', borderRadius: '4px', pointerEvents: 'none' }}
              fill
            />
          )}
        </Flex>

        <Flex flex={1} flexDir='column' marginBottom='-4px' marginLeft='-4px' marginRight='-4px' p={3}>
          <Heading
            as='a'
            color='black'
            fontFamily='body'
            fontSize='md'
            fontWeight='600'
            href={url}
            lineHeight='1.375rem'
            noOfLines={!enrollment && hasCatalogItem && hasReviews ? 3 : 4}
            sx={{
              '&::before': {
                display: 'block',
                content: '""',
                position: 'absolute',
                inset: 0,
                zIndex: 1,
              },
            }}
          >
            {catalogTitle}
          </Heading>

          {!enrollment &&
            hasCatalogItem &&
            (hasReviews || props.catalogItem.rawDurationDisplay || props.catalogItem.difficultyLevel) && (
              <Stack marginTop='auto' spacing='12px'>
                {hasReviews && (
                  <Flex gap='4px'>
                    <Rating color='#CA8702' rating={props.catalogItem.reviewSummary.starsAverage} size='sm' />
                    <Text color='gray.700' fontWeight='600' size='caption'>
                      ({props.catalogItem.reviewSummary.numberOfReviews})
                    </Text>
                  </Flex>
                )}
                <Flex borderTop='1px solid' borderTopColor='gray.100' flexDir='column' pt={3}>
                  <Flex alignItems='center' color='gray.700' flexBasis='18px' fontWeight='500' gap={1}>
                    {localeRawDuration && (
                      <>
                        <TimeIcon />
                        <Text size='caption' textTransform='capitalize'>
                          {localeRawDuration}
                        </Text>
                      </>
                    )}
                  </Flex>
                  <Flex alignItems='center' color='gray.700' flexBasis='18px' fontWeight='500' gap={1}>
                    {localeDifficultyLevel && (
                      <>
                        {(props.catalogItem.difficultyLevel === 'Beginner' ||
                          props.catalogItem.difficultyLevel === 'Discovery' ||
                          props.catalogItem.difficultyLevel === 'Fluency') && <Difficulty1Icon />}
                        {props.catalogItem.difficultyLevel === 'Intermediate' && <Difficulty2Icon />}
                        {props.catalogItem.difficultyLevel === 'Advanced' && <Difficulty3Icon />}
                        <Text size='caption'>{localeDifficultyLevel}</Text>
                      </>
                    )}
                  </Flex>
                </Flex>
              </Stack>
            )}

          {enrollment && (
            <Flex flex='1' flexDir='column'>
              <Flex alignItems='flex-end' flex='1' gap='8px' marginTop='auto'>
                {!isReadyForGraduation && isConsumerSubscriptionPaused && (
                  <Box position='relative' zIndex={2}>
                    <SubscriptionPausedModal
                      isOpen={isPausedModalOpen}
                      paymentDate={formattedResumeDate}
                      onClose={closePausedModal}
                    />
                    <PlayButton
                      ariaLabel={t('settings.subscription.resumeSubscription')!}
                      overrideColor='gray.500'
                      onClick={openPausedModal}
                    />
                  </Box>
                )}
                {!isReadyForGraduation && !isGraduated && !isConsumerSubscriptionPaused && isInProgress && (
                  <PlayProgressBar
                    percentComplete={programProgress?.completion ?? 0}
                    playButtonHref={`${enrollment.classroomLink}/resume-learning`}
                    progressAriaLabel={t('program.progressIs', { percent: programProgress?.completion ?? 0 })!}
                    playButtonAriaLabel={
                      t('program.continueProgram', {
                        programName: catalogTitle,
                      })!
                    }
                    onPlayButtonClick={() => {
                      trackEvent({
                        type: 'click',
                        name: 'catalogCtaClicked',
                        action: 'program_card_clicked',
                        label: enrollment.key,
                        value: 'resume',
                      });
                    }}
                  />
                )}
              </Flex>
              {notStarted && (
                <ButtonLink
                  buttonProps={{
                    colorScheme: 'blue',
                    width: 'full',
                    mt: '4px',
                    size: 'sm',
                    zIndex: 2,
                    textAlign: 'center',
                    height: 'auto',
                    padding: '8px',
                    whiteSpace: 'pre-wrap',
                    onClick: () => {
                      trackEvent({
                        type: 'click',
                        name: 'catalogCtaClicked',
                        action: 'program_card_clicked',
                        label: enrollment.key,
                        value: 'start_learning',
                      });
                    },
                  }}
                  linkProps={{
                    href: `${enrollment.classroomLink}/resume-learning`,
                  }}
                >
                  {t('program.startLearning')}
                </ButtonLink>
              )}
              {isReadyForGraduation && (
                <ButtonLink
                  buttonProps={{
                    colorScheme: 'blue',
                    width: 'full',
                    mt: '4px',
                    size: 'sm',
                    zIndex: 2,
                    textAlign: 'center',
                    height: 'auto',
                    padding: '8px',
                    whiteSpace: 'pre-wrap',
                    onClick: () => {
                      trackEvent({
                        type: 'click',
                        name: 'catalogCtaClicked',
                        action: 'program_card_clicked',
                        label: enrollment.key,
                        value: 'graduate',
                      });
                    },
                  }}
                  linkProps={{
                    href: `${publicEnv.NEXT_PUBLIC_LEARN_URL}/graduation/${enrollment.key}`,
                  }}
                >
                  {t('program.graduateNow')}
                </ButtonLink>
              )}
              {isGraduated && (
                <ButtonLink
                  buttonProps={{
                    colorScheme: 'blue',
                    width: 'full',
                    mt: '4px',
                    size: 'sm',
                    zIndex: 2,
                    variant: 'outline',
                    textAlign: 'center',
                    height: 'auto',
                    padding: '8px',
                    whiteSpace: 'pre-wrap',
                    onClick: () => {
                      trackEvent({
                        type: 'click',
                        name: 'catalogCtaClicked',
                        action: 'program_card_clicked',
                        label: enrollment.key,
                        value: 'review_content',
                      });
                    },
                  }}
                  linkProps={{
                    href: `${enrollment.classroomLink}/resume-learning`,
                  }}
                >
                  {t('program.reviewContent')}
                </ButtonLink>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      {!!Tags && (
        <Flex
          flexWrap='wrap'
          gap='8px'
          left='12px'
          pointerEvents='none'
          position='absolute'
          right='62px'
          top='12px'
          zIndex={1}
        >
          {Tags}
        </Flex>
      )}

      {userId && (
        <Box position='absolute' right='12px' top='12px' zIndex={2}>
          {shouldSeeUnenrollOption ? (
            <OptionsMenu
              programKey={hasCatalogItem ? props.catalogItem.key : props.basicItem.key}
              programTitle={catalogTitle}
            />
          ) : (
            <FavoriteButton
              programKey={hasCatalogItem ? props.catalogItem.key : props.basicItem.key}
              programTitle={catalogTitle}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export function CatalogCardSkeleton({
  width = '190px',
  maxWidth = 'unset',
  type = 'catalog',
}: {
  width?: string | BreakpointValue<string>;
  maxWidth?: string | BreakpointValue<string>;
  type?: 'catalog' | 'enrollment';
}) {
  return (
    <Flex
      bg='blue.800'
      borderRadius='base'
      flexDirection='column'
      flexShrink={0}
      height='292px'
      maxWidth={maxWidth}
      minWidth='152px'
      overflow='hidden'
      padding={1}
      width={width}
    >
      <Skeleton borderRadius='base' flex='0 1 100px' />
      <Stack flex='1' padding={2} spacing='24px'>
        <Stack spacing='4px'>
          <Skeleton h='16px' />
          <Skeleton h='16px' width='65%' />
        </Stack>
        {type === 'catalog' && (
          <>
            <Flex alignItems='center' gap={1} mt='auto'>
              <Rating isLoading={true} size='sm' />
              <Skeleton h='12px' width='24px' />
            </Flex>
            <Flex flexDir='column' gap={2}>
              <Flex gap={1}>
                <Skeleton h='12px' width='15px' />
                <Skeleton h='12px' width='56px' />
              </Flex>
              <Flex gap={1}>
                <Skeleton h='12px' width='15px' />
                <Skeleton h='12px' width='64px' />
              </Flex>
            </Flex>
          </>
        )}
        {type === 'enrollment' && <Skeleton h='36px' mt='auto' width='100%' />}
      </Stack>
    </Flex>
  );
}

export function isCatalogCardWithCatalogItem(
  catalogCardProps: CatalogCardWithCatalogItem | CatalogCardWithBasicItem
): catalogCardProps is CatalogCardWithCatalogItem {
  return catalogCardProps.catalogItem !== undefined;
}

export function OptionsMenu({ programKey, programTitle }: { programKey: string; programTitle: string | null }) {
  const { t } = useI18n();
  const toast = useToast();
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  async function handleRemoveEnrollment() {
    try {
      await deleteEnrollment(programKey);
      // Note: when removing an enrollment, we need to remove the program from cached catalog search results after successful response.
      const cachedEnrolledProgramsQueryData =
        queryClient.getQueriesData('catalog')?.filter(([key]) => {
          const queryKey = key as [string, { scope?: string[] }];
          return queryKey[1]?.scope?.some((scope) => scope === 'enrolled' || scope === 'completed');
        }) || [];
      for (const [key, value] of cachedEnrolledProgramsQueryData as [string, CatalogSearchResults][]) {
        const prevSearchResultItems = value?.searchResultItems;
        if (prevSearchResultItems) {
          const newSearchResultItems = prevSearchResultItems.filter((item) => item.key !== programKey);
          const nextNumberOfHits = value.numberOfHits - 1;
          queryClient.setQueryData(key, {
            ...value,
            searchResultItems: newSearchResultItems,
            numberOfHits: nextNumberOfHits,
            numberOfPages: value.pageSize > 0 ? Math.ceil(nextNumberOfHits / value.pageSize) : 0,
          });
        }
      }

      router.replace(router.asPath);

      toast({
        variant: 'success',
        title: t('program.enrollmentRemoved'),
      });
    } catch (error) {
      if (error instanceof DeleteEnrollmentError && error.name === DELETE_ENROLLMENT_POLICY_ERROR) {
        toast({
          variant: 'error',
          title: t('program.errorRemovingEnrollmentBlockedByPolicy'),
        });
        return;
      }
      toast({
        variant: 'error',
        title: t('program.errorRemovingEnrollment'),
      });
    }
  }

  function handleMenuClose() {
    // when closing the menu it will add focus to the card causing the carousel to slide putting the card in the first position
    // to prevent this we force the focus to go to the next or prev button
    // @ts-ignore
    containerRef.current?.closest('.swiper')?.querySelector('.swiper-button-container button:not([disabled])')?.focus();
  }

  return (
    <Box ref={containerRef} onClick={(e) => e.stopPropagation()}>
      <Menu placement='bottom-end' onClose={handleMenuClose}>
        <MenuButton
          _focusVisible={{ outlineColor: 'white', outlineOffset: '-6px' }}
          _hover={{ bgColor: 'rgba(11 ,11 ,11 ,0.9)' }}
          aria-label={t('program.enrollmentOptions') ?? ''}
          as={IconButton}
          bgColor='rgba(11, 11, 11, 0.6)'
          border='solid 2px'
          borderColor='white'
          color='white'
          icon={<MoreHorizontalIcon height='24px' width='24px' />}
          size='sm'
          variant='solid'
          zIndex={2}
          isRound
        />
        <Portal>
          <MenuList fontSize='sm'>
            <FavoriteMenuItem programKey={programKey} programTitle={programTitle} />
            <MenuItem
              aria-label={t('program.removeEnrollmentAriaLabel', { programTitle })!}
              py='4px'
              onClick={handleRemoveEnrollment}
            >
              {t('program.removeEnrollment')}
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    </Box>
  );
}
